/* ==========================================================================
   <cheo-product-form> — talla obligatoria y barra de compra fija (Calzado Cheo)

   Envuelve la columna de información de la ficha. Resuelve tres cosas que Dawn
   no trae y que el diseño pide (design/README.md §10, screenshots 10 y 12):

   1. Talla obligatoria. Shopify siempre preselecciona una variante, así que la
      ficha nace con una talla puesta que el usuario nunca eligió. Aquí se marca
      la selección como «pendiente»: los chips salen sin resaltado y el botón
      dice «Selecciona una talla» hasta el primer clic real.
   2. Validación visible. Mientras está pendiente el botón lleva
      `aria-disabled="true"` —no `disabled`— para que el clic siga llegando y se
      pueda pintar el error: borde rojo en los chips, mensaje y foco al primero.
      `product-form.js` de Dawn ya ignora los envíos con `aria-disabled`.
   3. Barra de compra fija en móvil. Espeja precio, talla y estado del botón real
      en lugar de duplicar el formulario (dos <form> con el mismo id romperían
      tanto el envío como el `form=` de los radios).

   La talla pendiente se guarda como atributo del propio elemento y no dentro de
   `variant-selects`, porque Dawn reemplaza ese nodo entero en cada cambio de
   variante y el estado se perdería.
   ========================================================================== */

class CheoProductForm extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.sizeGroup = this.querySelector('[data-size-group]');
    this.errorEl = this.querySelector('[data-size-error]');
    this.stickyBar = this.dataset.sticky ? document.querySelector(this.dataset.sticky) : null;

    this.pending = this.dataset.requireSize === 'true' && this.sizeGroup !== null;
    if (this.pending) this.setAttribute('data-size-pending', '');

    this.onSizeInteraction = this.onSizeInteraction.bind(this);
    this.onSubmitAttempt = this.onSubmitAttempt.bind(this);

    /* El clic (y no el `change`) porque si el usuario pulsa el chip que Shopify
       ya traía marcado no hay cambio de estado y `change` nunca dispara. */
    this.addEventListener('click', this.onSizeInteraction);
    this.addEventListener('keyup', this.onSizeInteraction);

    this.applyPendingState();
    this.syncWhatsapp();
    this.initSticky();

    /* Dawn reemplaza el botón de compra y el precio en cada cambio de variante:
       hay que reimponer el estado pendiente y volver a espejar la barra. */
    this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      this.applyPendingState();
      this.syncWhatsapp(event?.data?.html);
      this.syncSticky();
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onSizeInteraction);
    this.removeEventListener('keyup', this.onSizeInteraction);
    this.submitButton?.removeEventListener('click', this.onSubmitAttempt);
    this.stickyButton?.removeEventListener('click', this.onStickyClick);
    this.buttonObserver?.disconnect();
    this.unsubscribeVariant?.();
  }

  get submitButton() {
    return this.querySelector(`#ProductSubmitButton-${this.sectionId}`);
  }

  get checkedSize() {
    return this.sizeGroup?.querySelector('input:checked');
  }

  get whatsappButton() {
    return this.querySelector('[data-whatsapp-buy]');
  }

  /* ---- 1 y 2. Talla obligatoria -------------------------------------- */

  onSizeInteraction(event) {
    if (!this.pending) return;
    if (!this.sizeGroup?.contains(event.target)) return;
    /* En teclado el chip se recorre con flechas; solo Espacio/Enter confirman. */
    if (event.type === 'keyup' && event.key !== ' ' && event.key !== 'Enter') return;

    this.pending = false;
    this.removeAttribute('data-size-pending');
    this.removeAttribute('data-size-invalid');
    this.applyPendingState();
    this.syncWhatsapp();
    this.syncSticky();
  }

  /* ---- «Comprar por WhatsApp» con la talla ---------------------------- */

  /* El mensaje no se arma aquí: lo escribe Liquid desde los locales y llega en
     `data-href-size` ya codificado. Al cambiar de variante Dawn nos entrega la
     sección recién renderizada por el servidor, así que basta con copiar de ahí
     el href nuevo —el del `data-` en el DOM se quedó en la talla anterior—.
     Mientras la talla siga pendiente vale el mensaje sin talla: el comprador no
     ha elegido nada y el mensaje no debe decir que sí. */
  syncWhatsapp(html) {
    const link = this.whatsappButton;
    if (!link) return;

    if (html) {
      const fresh = html.querySelector('[data-whatsapp-buy]');
      if (fresh?.dataset.hrefSize) link.dataset.hrefSize = fresh.dataset.hrefSize;
    }

    const next = this.pending ? link.dataset.hrefPlain : link.dataset.hrefSize;
    if (next) link.href = next;
  }

  applyPendingState() {
    const button = this.submitButton;
    if (!button) return;

    button.removeEventListener('click', this.onSubmitAttempt);

    if (!this.pending) {
      button.removeAttribute('aria-disabled');
      return;
    }

    button.setAttribute('aria-disabled', 'true');
    button.addEventListener('click', this.onSubmitAttempt);

    const label = button.querySelector('span');
    if (label && this.dataset.i18nChooseSizeCta) label.textContent = this.dataset.i18nChooseSizeCta;
  }

  onSubmitAttempt(event) {
    if (!this.pending) return;
    event.preventDefault();

    this.setAttribute('data-size-invalid', '');
    if (this.errorEl) this.errorEl.hidden = false;

    /* Foco al primer chip disponible, no al primero a secas: enviar el foco a una
       talla agotada deja al usuario en un control que no puede usar. */
    const first = this.sizeGroup?.querySelector('input:not([disabled]):not(.disabled)');
    first?.focus();
  }

  /* ---- 3. Barra de compra fija --------------------------------------- */

  initSticky() {
    if (!this.stickyBar) return;

    this.stickyButton = this.stickyBar.querySelector('[data-sticky-submit]');
    this.onStickyClick = () => this.submitButton?.click();
    this.stickyButton?.addEventListener('click', this.onStickyClick);

    /* `is-enabled` la pone el JS: sin él la barra no aparece y el botón de la
       columna sigue siendo el único, así que la ficha funciona sin JavaScript. */
    this.stickyBar.hidden = false;
    this.stickyBar.classList.add('is-enabled');
    this.classList.add('cheo-product-form--has-sticky');

    this.buttonObserver = new MutationObserver(() => this.syncStickyButton());
    if (this.submitButton) {
      this.buttonObserver.observe(this.submitButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
      });
    }

    this.syncSticky();
  }

  syncSticky() {
    if (!this.stickyBar) return;

    const priceSource = this.querySelector(`#price-${this.sectionId}`);
    const priceTarget = this.stickyBar.querySelector('[data-sticky-price]');
    if (priceSource && priceTarget) {
      /* Copiamos solo el precio. La nota de envío gratis vive dentro del mismo
         nodo `#price-…` (main-product.liquid, bloque «price») porque es lo único
         que product-info refresca al cambiar de variante; arrastrarla a la barra
         fija le añade una línea larga a la columna izquierda y empuja «Agregar
         al carrito» fuera de la pantalla en móvil. */
      const clone = priceSource.cloneNode(true);
      clone.querySelectorAll('.cheo-product__shipping').forEach((node) => node.remove());
      priceTarget.innerHTML = clone.innerHTML;
    }

    const sizeTarget = this.stickyBar.querySelector('[data-sticky-size]');
    if (sizeTarget) {
      const value = this.pending ? null : this.checkedSize?.value;
      sizeTarget.textContent = value ? this.dataset.i18nSize?.replace('{size}', value) || value : '';
    }

    this.syncStickyButton();
  }

  syncStickyButton() {
    const button = this.submitButton;
    if (!button || !this.stickyButton) return;

    const label = this.stickyButton.querySelector('span');
    const sourceLabel = button.querySelector('span');
    if (label && sourceLabel) label.textContent = sourceLabel.textContent;

    this.stickyButton.disabled = button.hasAttribute('disabled');
    this.stickyButton.classList.toggle('loading', button.classList.contains('loading'));

    if (button.getAttribute('aria-disabled') === 'true') {
      this.stickyButton.setAttribute('aria-disabled', 'true');
    } else {
      this.stickyButton.removeAttribute('aria-disabled');
    }
  }
}

if (!customElements.get('cheo-product-form')) {
  customElements.define('cheo-product-form', CheoProductForm);
}
